import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '../lib/language'

export default function AboutPage() {
  const { t, language } = useLanguage()
  const isEs = language === 'es'
  const [scrolled, setScrolled] = useState(false)
  const [showTop, setShowTop] = useState(false)

  useEffect(() => {
    function onScroll() {
      setScrolled(window.scrollY > 60)
      setShowTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  function scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }) }

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&family=DM+Sans:wght@300;400;500&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }

    /* ── ADA ── */
    .sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
    .lca-skip { position:absolute; top:-100px; left:16px; background:#C9A84C; color:#0D1B3E; padding:8px 16px; border-radius:8px; font-size:13px; font-weight:600; text-decoration:none; z-index:9999; transition:top .2s; }
    .lca-skip:focus { top:16px; }
    :focus-visible { outline:2px solid #C9A84C; outline-offset:3px; border-radius:4px; }

    /* ── NAV ── */
    .lca-nav { position:fixed; top:0; left:0; right:0; z-index:100; padding:18px 48px; display:flex; align-items:center; justify-content:space-between; background:linear-gradient(to bottom,rgba(0,0,0,0.7),transparent); transition:background 0.4s; }
    .lca-nav.scrolled { background:rgba(13,27,62,0.97); backdrop-filter:blur(8px); }
    .lca-nav-brand { display:flex; flex-direction:column; text-decoration:none; }
    .lca-nav-name { font-family:'Cormorant Garamond',serif; font-size:22px; font-weight:600; color:#C9A84C; letter-spacing:0.02em; line-height:1.2; }
    .lca-nav-sub { font-size:8px; color:rgba(201,168,76,0.55); letter-spacing:0.15em; margin-top:3px; }
    .lca-nav-links { display:flex; gap:28px; align-items:center; }
    .lca-nav-link { color:rgba(255,255,255,0.75); font-size:12px; letter-spacing:0.05em; text-decoration:none; transition:color 0.2s; }
    .lca-nav-link:hover { color:#C9A84C; }
    .lca-nav-link.active { color:#C9A84C; font-weight:500; }
    .lca-nav-signin { color:#C9A84C; font-size:12px; font-weight:500; background:transparent; padding:0; border:none; cursor:pointer; letter-spacing:0.08em; text-decoration:none; }
    @media(max-width:900px){ .lca-nav { padding:14px 24px; } .lca-nav-links { gap:18px; } .lca-nav-name { font-size:18px; } }
    @media(max-width:768px){ .lca-nav { padding:12px 20px; } .lca-nav-name { font-size:15px; } .lca-nav-sub { display:none; } .lca-nav-links { gap:12px; } .lca-nav-link { font-size:11px; } }
    @media(max-width:580px){ .lca-nav-name { font-size:13px; } .lca-nav-links .lca-nav-link { display:none; } .lca-nav-links { gap:10px; } }

    /* ── HERO ── */
    .lca-hero { padding:120px 48px 64px; text-align:center; border-bottom:1px solid rgba(255,255,255,0.06); background:#0D1B3E; }
    .lca-hero-eyebrow { font-size:10px; letter-spacing:0.2em; color:#C9A84C; margin-bottom:14px; }
    .lca-hero-rule { width:40px; height:1px; background:#C9A84C; margin:0 auto 22px; opacity:0.8; }
    .lca-hero h1 { font-family:'Cormorant Garamond',serif; font-size:40px; font-weight:400; color:#fff; margin-bottom:14px; line-height:1.15; }
    .lca-hero h1 span { color:#C9A84C; }
    .lca-hero-sub { font-size:14px; color:rgba(255,255,255,0.5); max-width:500px; margin:0 auto; line-height:1.85; }
    @media(max-width:640px){ .lca-hero { padding:100px 20px 48px; } .lca-hero h1 { font-size:26px; } }

    /* ── ABOUT SECTION ── */
    .lca-about { padding:80px 48px; max-width:1100px; margin:0 auto; }
    @media(max-width:640px){ .lca-about { padding:48px 20px; } }

    /* ── PHOTO + TEXT LAYOUT ── */
    .lca-about-grid { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:start; margin-bottom:80px; }
    @media(max-width:860px){ .lca-about-grid { grid-template-columns:1fr; gap:40px; } }

    /* ── PHOTO SIDE ── */
    .lca-photo-wrap { display:flex; flex-direction:column; align-items:center; }
    .lca-photo { width:100%; max-width:580px; border-radius:12px; overflow:hidden; border:1px solid rgba(201,168,76,0.2); box-shadow:0 20px 60px rgba(0,0,0,0.4); margin-bottom:20px; }
    .lca-photo img { width:100%; height:auto; display:block; object-fit:cover; }
    .lca-founder-name { font-family:'Cormorant Garamond',serif; font-size:24px; font-weight:600; color:#fff; margin-bottom:4px; text-align:center; }
    .lca-founder-title { font-size:13px; color:#C9A84C; letter-spacing:0.05em; text-align:center; }

    /* ── TEXT SIDE ── */
    .lca-about-text { padding-top:8px; }
    .lca-quote { font-family:'Cormorant Garamond',serif; font-size:20px; font-weight:500; color:#fff; line-height:1.55; margin-bottom:28px; padding-left:20px; border-left:3px solid #C9A84C; }
    .lca-about-body { font-size:14px; color:rgba(255,255,255,0.6); line-height:1.9; margin-bottom:18px; }

    /* ── CORE VALUES ── */
    .lca-values { margin-bottom:80px; }
    .lca-values-title { font-family:'Cormorant Garamond',serif; font-size:32px; font-weight:400; color:#C9A84C; text-align:center; margin-bottom:48px; }
    .lca-values-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:24px; }
    @media(max-width:900px){ .lca-values-grid { grid-template-columns:repeat(2,1fr); } }
    @media(max-width:480px){ .lca-values-grid { grid-template-columns:1fr; } }
    .lca-value-card { text-align:center; padding:32px 20px; background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.06); border-radius:14px; transition:border-color 0.2s; }
    .lca-value-card:hover { border-color:rgba(201,168,76,0.25); }
    .lca-value-icon { display:flex; align-items:center; justify-content:center; margin:0 auto 20px; width:56px; height:56px; }
    .lca-value-icon svg { width:40px; height:40px; fill:#C9A84C; }
    .lca-value-name { font-size:16px; font-weight:500; color:#fff; margin-bottom:12px; }
    .lca-value-desc { font-size:12px; color:rgba(255,255,255,0.45); line-height:1.75; }

    /* ── NATIONWIDE VISION ── */
    .lca-vision { margin-bottom:80px; text-align:center; padding:48px 40px; background:rgba(255,255,255,0.02); border:1px solid rgba(255,255,255,0.07); border-radius:16px; }
    .lca-vision-title { font-family:'Cormorant Garamond',serif; font-size:32px; font-weight:400; color:#fff; margin-bottom:6px; line-height:1.2; }
    .lca-vision-title span { color:#C9A84C; }
    .lca-vision-rule { width:80px; height:2px; background:#C9A84C; margin:0 auto 24px; border-radius:2px; opacity:0.6; }
    .lca-vision-desc { font-size:15px; color:rgba(255,255,255,0.55); max-width:680px; margin:0 auto; line-height:1.9; }

    /* ── CTA ── */
    .lca-cta { padding:56px 48px; text-align:center; border-top:1px solid rgba(255,255,255,0.06); }
    .lca-cta h2 { font-family:'Cormorant Garamond',serif; font-size:28px; font-weight:400; color:#fff; margin-bottom:10px; }
    .lca-cta p { font-size:13px; color:rgba(255,255,255,0.45); margin-bottom:24px; line-height:1.7; }
    /* ✅ CTA PASS — gold gradient, white text, 10px radius, shadow, hover lift */
    .lca-cta-btn { background:linear-gradient(135deg,#8A5C00 0%,#C9A84C 50%,#B8941F 100%); color:#fff; padding:14px 36px; border-radius:10px; font-size:13px; font-weight:600; border:none; cursor:pointer; letter-spacing:0.07em; box-shadow:0 4px 14px rgba(13,27,62,0.3); text-decoration:none; display:inline-block; transition:transform 0.15s, box-shadow 0.2s; }
    .lca-cta-btn:hover { transform:translateY(-2px); box-shadow:0 6px 20px rgba(13,27,62,0.4); }
    .lca-cta-btn:focus-visible { outline:3px solid #fff; outline-offset:3px; }
    @media(max-width:640px){ .lca-cta { padding:40px 20px; } .lca-cta h2 { font-size:22px; } .lca-vision { padding:32px 20px; } }

    /* ── BACK TO TOP ── */
    .lca-back-top-wrap { background: #0D1B3E; display: flex; justify-content: center; padding: 16px 0 0; }
    .lca-back-top { position:fixed; bottom:24px; right:20px; width:44px; height:44px; border-radius:50%; background:#C9A84C; border:none; cursor:pointer; display:flex; align-items:center; justify-content:center; font-size:18px; color:#0D1B3E; font-weight:700; box-shadow:0 4px 16px rgba(0,0,0,0.3); transition:all 0.3s; opacity:0; pointer-events:none; z-index:150; }
    .lca-back-top.visible { opacity:1; pointer-events:auto; }
    .lca-back-top:hover { transform:translateY(-4px); box-shadow:0 6px 20px rgba(0,0,0,0.4); }

    /* ── FOOTER ── */
    .lca-footer { background:#0D1B3E; border-top:1px solid rgba(201,168,76,0.15); padding:32px 48px 28px; text-align:center; }
    .lca-footer-logo { display:inline-flex; align-items:center; gap:12px; text-decoration:none; margin-bottom:24px; }
    .lca-footer-logo-circle { width:48px; height:48px; border-radius:50%; border:1px solid rgba(201,168,76,0.3); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
    .lca-footer-logo-circle img { width:44px; height:44px; object-fit:contain; }
    .lca-footer-brand-name { font-family:'Cormorant Garamond',serif; font-size:15px; color:rgba(255,255,255,0.55); letter-spacing:0.04em; display:block; text-align:left; }
    .lca-footer-brand-sub { font-size:8px; color:rgba(201,168,76,0.45); letter-spacing:0.12em; margin-top:2px; display:block; text-align:left; }
    .lca-footer-links { display:flex; gap:24px; justify-content:center; flex-wrap:wrap; margin-bottom:24px; }
    .lca-footer-links a { font-size:11px; color:rgba(255,255,255,0.3); text-decoration:none; letter-spacing:0.04em; transition:color 0.2s; }
    .lca-footer-links a:hover { color:#C9A84C; }
    .lca-footer-bottom { border-top:1px solid rgba(255,255,255,0.06); padding-top:18px; }
    .lca-footer-copy { font-size:10px; color:rgba(255,255,255,0.2); margin-bottom:6px; }
    .lca-footer-veteran { font-size:11px; color:rgba(201,168,76,0.5); letter-spacing:0.05em; }
    @media(max-width:520px){ .lca-footer { padding:32px 20px 20px; } .lca-footer-links { gap:14px; } }
  `

  const PrecisionIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
    </svg>
  )
  const TransparencyIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
    </svg>
  )
  const RelationshipsIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  )
  const IntegrityIcon = () => (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
    </svg>
  )

  const coreValues = [
    { icon: <PrecisionIcon />, name_en: 'Precision', name_es: 'Precisión', desc_en: 'Every document is reviewed with meticulous attention to ensure accuracy and compliance.', desc_es: 'Cada documento es revisado con atención meticulosa para garantizar precisión y cumplimiento.' },
    { icon: <TransparencyIcon />, name_en: 'Transparency', name_es: 'Transparencia', desc_en: 'Clear communication and honest pricing with no hidden fees or surprises.', desc_es: 'Comunicación clara y precios honestos sin cargos ocultos ni sorpresas.' },
    { icon: <RelationshipsIcon />, name_en: 'Relationships', name_es: 'Relaciones', desc_en: 'We build lasting partnerships with clients, agents, and industry professionals.', desc_es: 'Construimos asociaciones duraderas con clientes, agentes y profesionales de la industria.' },
    { icon: <IntegrityIcon />, name_en: 'Integrity', name_es: 'Integridad', desc_en: 'Background-checked, insured agents you can trust with your most important transactions.', desc_es: 'Agentes con verificación de antecedentes y asegurados en quienes puedes confiar.' },
  ]

  return (
    <div style={{ background: '#0D1B3E', minHeight: '100vh', color: '#fff' }}>
      <style>{styles}</style>
      <a href="#about-main" className="lca-skip">Skip to main content</a>

      {/* NAV */}
      <nav className={`lca-nav${scrolled ? ' scrolled' : ''}`} role="navigation" aria-label="Main navigation">
        <Link to="/" className="lca-nav-brand" aria-label="Loan Closing Agent LLC — Go to homepage">
          <span className="lca-nav-name">Loan Closing Agent LLC</span>
        </Link>
        <div className="lca-nav-links">
          <Link to="/" className="lca-nav-link">{isEs ? 'Inicio' : 'Home'}</Link>
          <Link to="/services" className="lca-nav-link">{t('nav_services')}</Link>
          <Link to="/about" className="lca-nav-link active">{t('nav_about')}</Link>
          <Link to="/contact" className="lca-nav-link">{t('nav_contact')}</Link>
          <Link to="/login" className="lca-nav-signin" aria-label={t('nav_signin')}>{t('nav_signin')}</Link>
        </div>
      </nav>

      <main id="about-main" tabIndex={-1}>
        {/* HERO */}
        <section className="lca-hero" aria-labelledby="about-heading">
          <div className="lca-hero-eyebrow" aria-hidden="true">{isEs ? 'CONÓCENOS MÁS' : 'LEARN MORE ABOUT US'}</div>
          <div className="lca-hero-rule" aria-hidden="true" />
          <h1 id="about-heading">
            {isEs ? 'Construido sobre' : 'Built on'}{' '}
            <span>{isEs ? 'Experiencia' : 'Experience'}</span><br />
            {isEs ? 'Impulsado por Servicio' : 'Driven by Service'}
          </h1>
          <p className="lca-hero-sub">
            {isEs
              ? <>Desde 2019, conectando personas y empresas con<br />Agentes Notariales de Firma Certificados en todo el país.</>
              : <>Since 2019, connecting people and businesses with<br />Certified Notary Signing Agents across the nation.</>}
          </p>
        </section>

        <div className="lca-about">
          {/* PHOTO + TEXT */}
          <div className="lca-about-grid">
            <div className="lca-photo-wrap">
              <div className="lca-photo">
                <img src="/lca_about.jpg" alt="Nancy Phillips, Founder and CEO of Loan Closing Agent LLC, seated at her desk in a professional office setting." onError={e => { e.target.style.display = 'none' }} />
              </div>
              <div className="lca-founder-name">Nancy Phillips</div>
              <div className="lca-founder-title">Founder &amp; CEO</div>
            </div>
            <div className="lca-about-text">
              <blockquote className="lca-quote">
                {isEs
                  ? '"Construido sobre la experiencia e impulsado por el servicio, Loan Closing Agent LLC es su socio de confianza para firmas y notarizaciones en todo el país."'
                  : '"Built on experience and driven by service, Loan Closing Agent LLC is your trusted partner for nationwide signings and notarizations."'}
              </blockquote>
              <p className="lca-about-body">
                {isEs
                  ? 'Loan Closing Agent, LLC fue fundada en 2019 con una misión simple: proporcionar servicios confiables y profesionales de firma de préstamos, notaría móvil y notarización remota en línea en los que compañías de título, prestamistas e individuos puedan depender a nivel nacional.'
                  : 'Loan Closing Agent, LLC was founded in 2019 with a simple mission: to provide reliable, professional loan signing, mobile notary, and remote online notarization services that title companies, lenders, and individuals can depend on nationwide. Built on the founder\'s experience as a notary since 2008 and as a seasoned notary signing agent with extensive hands-on expertise, the company brings real-world industry knowledge to every transaction.'}
              </p>
              <p className="lca-about-body">
                {isEs
                  ? 'A través de nuestro DBA, Nationwide Notary Professionals Network, hemos construido una red creciente de agentes notariales de firma verificados y experimentados en los 50 estados.'
                  : 'Through our DBA, Nationwide Notary Professionals Network, we have built a growing network of vetted and experienced notary signing agents across all 50 states. Every agent in our network is background-checked, insured, and committed to delivering accurate, timely signings.'}
              </p>
              <p className="lca-about-body">
                {isEs
                  ? 'Entendemos la importancia de cada transacción. Ya sea el cierre de compra de un comprador de vivienda por primera vez, una transacción comercial compleja o una simple notarización de poder notarial, abordamos cada asignación con el mismo nivel de profesionalismo, precisión y cuidado.\n\nSobre todo, le agradecemos por confiar en nosotros con sus transacciones y firmas.'
                  : 'We understand the importance of every transaction. Whether it\'s a first-time homebuyer\'s purchase closing, a complex commercial transaction, or a simple power of attorney notarization, we approach every assignment with the same level of professionalism, precision, and care.\n\nAbove all, we thank you for trusting us with your transactions and signings. It\'s a responsibility we value deeply, and we are honored to support you with the highest level of service.'}
              </p>
            </div>
          </div>

          {/* CORE VALUES */}
          <section aria-labelledby="values-heading" className="lca-values">
            <h2 id="values-heading" className="lca-values-title">{isEs ? 'Nuestros Valores Fundamentales' : 'Our Core Values'}</h2>
            <div className="lca-values-grid">
              {coreValues.map((v, i) => (
                <div key={i} className="lca-value-card">
                  <div className="lca-value-icon">{v.icon}</div>
                  <div className="lca-value-name">{isEs ? v.name_es : v.name_en}</div>
                  <div className="lca-value-desc">{isEs ? v.desc_es : v.desc_en}</div>
                </div>
              ))}
            </div>
          </section>

          {/* NATIONWIDE VISION */}
          <section aria-labelledby="vision-heading" className="lca-vision">
            <h2 id="vision-heading" className="lca-vision-title">
              {isEs ? <>Nuestra <span>Visión</span> Nacional</> : <>Our Nationwide <span>Vision</span></>}
            </h2>
            <div className="lca-vision-rule" aria-hidden="true" />
            <p className="lca-vision-desc">
              {isEs
                ? 'Estamos creciendo nuestra red cada día para asegurarnos de que sin importar dónde en el país se necesite una firma, haya un agente calificado y de confianza cerca y listo.'
                : 'We\'re growing our network every day to ensure that no matter where in the country a signing is needed, a qualified, trusted agent is nearby and ready. Our vision is to be the go-to resource for loan signings and notary services nationwide.'}
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="lca-cta">
          <h2>{isEs ? '¿Listo para Unirse a Nosotros?' : 'Ready to Join Us?'}</h2>
          <p>
            {isEs
              ? 'Contáctanos hoy y descubre por qué cientos de clientes confían en Loan Closing Agent LLC.'
              : 'Contact us today and discover why hundreds of clients trust Loan Closing Agent LLC.'}
          </p>
          <Link to="/contact" className="lca-cta-btn" aria-label={isEs ? 'Contáctanos hoy' : 'Contact us today'}>
            {isEs ? 'Contáctanos Hoy' : 'Contact Us Today'}
          </Link>
        </div>
      </main>

      {/* BACK TO TOP */}
      <div className="lca-back-top-wrap">
        <button className={`lca-back-top${showTop ? ' visible' : ''}`} onClick={scrollToTop} aria-label="Scroll back to top" aria-hidden={!showTop} tabIndex={showTop ? 0 : -1}>↑</button>
      </div>

      {/* FOOTER */}
      <footer className="lca-footer" role="contentinfo">
        <Link to="/" className="lca-footer-logo" aria-label="Loan Closing Agent LLC — Go to homepage">
          <div className="lca-footer-logo-circle">
            <img src="/lca_logo_quill_map.png" alt="Loan Closing Agent LLC logo" onError={e => { e.target.style.display = 'none' }} />
          </div>
          <div>
            <span className="lca-footer-brand-name">Loan Closing Agent LLC</span>
            <span className="lca-footer-brand-sub">DBA NATIONWIDE NOTARY PROFESSIONALS NETWORK</span>
          </div>
        </Link>
        <nav className="lca-footer-links" aria-label="Footer navigation">
          <Link to="/">{isEs ? 'Inicio' : 'Home'}</Link>
          <Link to="/services">{t('footer_services')}</Link>
          <Link to="/about">{t('footer_about')}</Link>
          <Link to="/contact">{t('footer_contact')}</Link>
          <Link to="/accessibility">{t('footer_accessibility')}</Link>
          <Link to="/login">{t('footer_signin')}</Link>
        </nav>
        <div className="lca-footer-bottom">
          <div className="lca-footer-copy">{t('footer_copy')}</div>
          <div className="lca-footer-veteran">{t('footer_veteran')}</div>
          <div style={{ fontSize:10, color:'rgba(255,255,255,0.25)', marginTop:6, fontStyle:'italic' }}>
            Loan Closing Agent, LLC is not a law firm and does not provide legal advice.
          </div>
        </div>
      </footer>
    </div>
  )
}
